/**
 * M02 Intent 列表 · S2 Day 6b
 *
 * 数据源：useIntentList → GET /admin/v1/intent/list
 * 显示：P11 P0 13 intent 行 · 中英描述 · service_stage · L1/L2 识别路径边界
 */
import { useIntentList } from "@/lib/api/intent";
import { intentLabel } from "@/lib/product-labels";

const SERVICE_STAGE_LABEL: Record<string, string> = {
	pre_sale: "售前",
	in_sale: "售中",
	after_sale: "售后",
	unknown: "其他",
};

const SERVICE_STAGE_TONE: Record<string, string> = {
	pre_sale: "bg-brand-info/10 text-brand-info border-brand-info/30",
	in_sale: "bg-brand-success/10 text-brand-success border-brand-success/30",
	after_sale: "bg-brand-warning/10 text-brand-warning border-brand-warning/30",
	unknown: "bg-surface-subtle text-text-muted border-surface-border",
};

export function IntentList() {
	const { data, isLoading, isError, error } = useIntentList();

	if (isLoading) {
		return (
			<div data-testid="intent-list-loading" className="p-4 text-sm text-text-secondary">
				加载 intent 列表中...
			</div>
		);
	}

	if (isError) {
		return (
			<div data-testid="intent-list-error" className="p-4 text-sm text-brand-danger">
				加载失败：{error instanceof Error ? error.message : String(error)}
			</div>
		);
	}

	const items = data?.items ?? [];

	return (
		<div className="space-y-3" data-testid="intent-list">
			<div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
				<div className="space-y-1 text-sm text-text-secondary">
					<div>
						规则 bundle <span className="font-mono text-xs">{data?.bundle_version}</span>
						<span className="ml-2">总规则数 {data?.total_rules ?? 0}</span>
					</div>
					<div className="text-xs text-text-muted">
						P11 P0 · 13 intent · L1 V0.3 业务语义主路径 · L2 仅保留高置信快路径
					</div>
				</div>
				<div className="flex flex-wrap gap-2 text-xs">
					<span className="rounded-md border border-brand-success/30 bg-brand-success/10 px-2 py-1 text-brand-success">
						L1 主路径：业务语义判断
					</span>
					<span className="rounded-md border border-brand-info/30 bg-brand-info/10 px-2 py-1 text-brand-info">
						L2 快路径：明确规则命中
					</span>
				</div>
			</div>
			<div className="overflow-x-auto rounded-md border border-surface-border">
				<table className="w-full border-collapse text-sm">
					<thead className="bg-surface-subtle">
						<tr className="text-left">
							<th className="px-3 py-2 font-medium">Intent</th>
							<th className="px-3 py-2 font-medium">阶段</th>
							<th className="px-3 py-2 font-medium">描述</th>
							<th className="px-3 py-2 font-medium">识别路径</th>
							<th className="px-3 py-2 font-medium">快路径触发词</th>
						</tr>
					</thead>
					<tbody>
						{items.map((item) => {
							const hasFastPath = item.l2_summary.enabled_count > 0;
							return (
								<tr
									key={item.intent}
									data-testid={`intent-row-${item.intent}`}
									className="border-t border-surface-border align-top"
								>
									<td className="px-3 py-2 text-sm font-medium text-text-primary">
										{intentLabel(item.intent)}
									</td>
									<td className="px-3 py-2">
										<span
											className={`inline-block rounded border px-2 py-0.5 text-xs ${
												SERVICE_STAGE_TONE[item.service_stage] ?? SERVICE_STAGE_TONE.unknown
											}`}
										>
											{SERVICE_STAGE_LABEL[item.service_stage] ?? item.service_stage}
										</span>
									</td>
									<td className="px-3 py-2">
										<div className="text-text-primary">{item.description_zh}</div>
										<div className="mt-0.5 text-xs text-text-muted">{item.description_en}</div>
									</td>
									<td className="px-3 py-2">
										<div
											className={`inline-flex rounded-md border px-2 py-1 text-xs font-medium ${
												hasFastPath
													? "border-brand-info/30 bg-brand-info/10 text-brand-info"
													: "border-brand-success/30 bg-brand-success/10 text-brand-success"
											}`}
										>
											{hasFastPath ? "L2 快路径" : "L1 主路径"}
										</div>
										<div className="mt-1 text-xs text-text-muted">
											{hasFastPath
												? `${item.l2_summary.enabled_count}/${item.l2_summary.rules_count} 启用`
												: "普通业务语义进入 L1 V0.3"}
										</div>
									</td>
									<td className="px-3 py-2">
										<div className="text-xs text-text-secondary">
											{item.l2_summary.sample_keywords_zh.length === 0 &&
											item.l2_summary.sample_keywords_en.length === 0 ? (
												<span className="text-text-muted">—</span>
											) : (
												<>
													<div>{item.l2_summary.sample_keywords_zh.join(" · ") || "—"}</div>
													<div className="text-text-muted">
														{item.l2_summary.sample_keywords_en.join(" · ") || "—"}
													</div>
												</>
											)}
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
}
